import { MouseEventHandler } from 'react'

import clsx from 'clsx'

import { ChildProps } from '@/common/util/props'

import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'

// Sync StatusColor
export const BadgeType = {
  SECONDARY: 'SECONDARY',
  DEFAULT: 'DEFAULT',
  SUCCESS: 'SUCCESS',
  EMPHASIS: 'EMPHASIS',
  EMPHASIS_SECONDARY: 'EMPHASIS_SECONDARY',
  REQUIRED: 'REQUIRED',
  CAUTION: 'CAUTION',
  CAUTION_SECONDARY: 'CAUTION_SECONDARY',
  CRITICAL: 'CRITICAL',
  DISABLED: 'DISABLED',
  NEGATIVE: 'NEGATIVE',
  KEYWORD: 'KEYWORD',
  KEYWORD_ON_BG_FILL: 'KEYWORD_ON_BG_FILL',
  TAG_CLICKABLE: 'TAG_CLICKABLE',
  TAG_CLICKABLE_EMPHASIS: 'TAG_CLICKABLE_EMPHASIS',
  UNSELECTED: 'UNSELECTED',
  AI: 'AI',
  SQUARE_PRIMARY: 'SQUARE_PRIMARY',
  SQUARE_SECONDARY: 'SQUARE_SECONDARY',
  SQUARE_CAUTION: 'SQUARE_CAUTION',
  SQUARE_CRITICAL: 'SQUARE_CRITICAL',
  SQUARE_SUCCESS: 'SQUARE_SUCCESS',
  SQUARE_TERTIARY: 'SQUARE_TERTIARY',
  SQUARE_TERTIARY_SECONDARY: 'SQUARE_TERTIARY_SECONDARY',
  SQUARE_DISABLED: 'SQUARE_DISABLED',
  SQUARE_BRAND: 'SQUARE_BRAND',
  SQUARE_BRAND_SECONDARY: 'SQUARE_BRAND_SECONDARY',
  SQUARE_AI: 'SQUARE_AI'
} as const

export type BadgeType = (typeof BadgeType)[keyof typeof BadgeType]

export const badgeTypeClass = (type: BadgeType): string => {
  switch (type) {
    case BadgeType.DEFAULT:
      return 'text-primary border-primary bg-fill-primary border-secondary'
    case BadgeType.SECONDARY:
      return 'text-secondary bg-fill-transparent-secondary border-primary'
    case BadgeType.EMPHASIS:
      return 'text-info bg-fill-info-secondary border-info'
    case BadgeType.EMPHASIS_SECONDARY:
      return 'text-info bg-surface border-info'
    case BadgeType.REQUIRED:
      return 'text-link bg-fill-primary border-focus'
    case BadgeType.SUCCESS:
      return 'text-success bg-fill-success-secondary border-success'
    case BadgeType.CAUTION:
      return 'text-caution bg-fill-caution-secondary border-caution'
    case BadgeType.CAUTION_SECONDARY:
      return 'text-caution bg-surface-caution border-caution'
    case BadgeType.CRITICAL:
      return 'text-critical bg-fill-critical-secondary border-critical-secondary'
    case BadgeType.NEGATIVE:
      return 'text-critical bg-fill-transparent-secondary border-critical'
    case BadgeType.KEYWORD:
      return 'text-info bg-fill-secondary border-none'
    case BadgeType.KEYWORD_ON_BG_FILL:
      return 'text-info bg-fill-primary border-secondary'
    case BadgeType.DISABLED:
      return 'text-disabled bg-fill-disabled border-disabled'
    case BadgeType.TAG_CLICKABLE:
      return 'text-primary bg-fill-tertiary'
    case BadgeType.TAG_CLICKABLE_EMPHASIS:
      return 'text-on-bg-fill bg-fill-brand'
    case BadgeType.UNSELECTED:
      return ''
    case BadgeType.AI:
      return 'text-ai bg-fill-ai-secondary'
    case BadgeType.SQUARE_PRIMARY:
      return 'border-ruri bg-ruri-secondary text-fill-brand'
    case BadgeType.SQUARE_SECONDARY:
      return 'border-disabled text-primary bg-surface-secondary'
    case BadgeType.SQUARE_CAUTION:
      return 'border-caution text-caution bg-caution'
    case BadgeType.SQUARE_CRITICAL:
      return 'text-critical bg-critical'
    case BadgeType.SQUARE_SUCCESS:
      return 'text-success bg-fill-success-secondary'
    case BadgeType.SQUARE_TERTIARY:
      return 'text-secondary bg-fill-tertiary'
    case BadgeType.SQUARE_TERTIARY_SECONDARY:
      return 'text-primary bg-fill-tertiary'
    case BadgeType.SQUARE_DISABLED:
      return 'border-disabled text-disabled bg-fill-disabled'
    case BadgeType.SQUARE_BRAND:
      return 'text-on-bg-fill bg-fill-primary-brand'
    case BadgeType.SQUARE_BRAND_SECONDARY:
      return 'border-brand-primary-square'
    case BadgeType.SQUARE_AI:
      return 'border-ai'
  }
}

interface Props extends ChildProps {
  size?: 10 | 12 | 14
  badgeType?: BadgeType
  connect?: boolean
  onClickClose?: MouseEventHandler<HTMLButtonElement>
  onClick?: MouseEventHandler<HTMLButtonElement>
  link?: string
  iconType?: IconType
  remnant?: boolean
  fixed?: boolean
  preWrap?: boolean
}

const Badge = ({
  children,
  size = 12,
  badgeType = BadgeType.DEFAULT,
  connect = false,
  iconType,
  onClickClose,
  onClick = undefined,
  remnant,
  fixed,
  preWrap = false
}: Props) => {
  const padding = size == 10 ? 'px-2 py-1' : onClickClose ? 'pl-2' : 'py-1 px-1.5'
  const isAi = badgeType == BadgeType.SQUARE_AI || badgeType == BadgeType.AI
  const isHierarchy = isAi || badgeType == BadgeType.SQUARE_BRAND_SECONDARY
  const rounded = badgeType.startsWith('SQUARE_') ? 'rounded-sm' : 'rounded-[80px]'

  const className = clsx(
    badgeTypeClass(badgeType),
    badgeType != BadgeType.UNSELECTED &&
      badgeType != BadgeType.SQUARE_TERTIARY &&
      badgeType != BadgeType.SQUARE_TERTIARY_SECONDARY &&
      badgeType != BadgeType.SQUARE_CRITICAL &&
      badgeType != BadgeType.SQUARE_SUCCESS &&
      'border',
    preWrap ? 'whitespace-pre-wrap' : 'whitespace-nowrap  max-w-[216px]',
    size == 10 ? 'text-size-10' : size == 12 ? 'text-size-12' : 'text-size-14',
    !isHierarchy ? padding : 'p-0.75',
    'leading-150 inline-flex items-center w-fit h-fit overflow-hidden',
    size == 10 ? 'min-h-[17px]' : 'min-h-[22px]',
    remnant && 'flex-1 min-w-0 max-w-fit',
    fixed && 'shrink-0',
    rounded
  )

  return (
    <>
      {onClickClose ? (
        <span className={clsx(className)}>
          <span className='block min-w-0 flex-1 truncate font-normal'>{children}</span>
          <button
            type='button'
            className={clsx(
              'outline-default bg-surface-transparent group grid h-5.5 w-5.5 shrink-0 place-content-center p-0.75 focus-visible:outline-offset-0',
              rounded
            )}
            onClick={onClickClose}
          >
            <Icon
              color={IconColor.PRIMARY}
              hoverColor={IconColor.PRIMARY}
              activeColor={IconColor.PRIMARY}
              type={IconType.CLOSE}
            />
          </button>
        </span>
      ) : badgeType == BadgeType.TAG_CLICKABLE || badgeType == BadgeType.TAG_CLICKABLE_EMPHASIS ? (
        <button
          type='button'
          className={clsx(
            className,
            badgeType == BadgeType.TAG_CLICKABLE
              ? 'hover:bg-fill-on-bg-fill-hover active:bg-fill-on-bg-fill-active'
              : 'hover:bg-fill-brand-hover active:bg-fill-brand-active'
          )}
          onClick={onClick}
        >
          <span className='block min-w-0 flex-1 truncate font-normal'>{children}</span>
        </button>
      ) : (
        <span
          className={clsx(
            className,
            onClick != undefined && 'min-w-0 flex-1',
            connect &&
              'after:border-l-shapes relative mr-0.5 rounded-tr-none rounded-br-none pr-0 after:absolute after:top-0 after:bottom-0 after:left-full after:border-y-11 after:border-l-8 after:border-y-transparent'
          )}
        >
          {isHierarchy ? (
            <span
              className={clsx(
                'z-10 flex',
                isAi && 'bg-fill-ai-secondary',
                isHierarchy && padding,
                rounded
              )}
            >
              <span
                className={clsx(
                  'block min-w-0 flex-1 truncate font-normal',
                  isAi ? 'text-ai' : 'text-brand-primary'
                )}
              >
                {children}
              </span>
            </span>
          ) : (
            <span className={'block min-w-0 flex-1 truncate font-normal'}>{children}</span>
          )}
          {iconType && (
            <Icon
              color={badgeType == BadgeType.CAUTION ? IconColor.CAUTION : IconColor.PRIMARY}
              type={iconType}
              line
            />
          )}
        </span>
      )}
    </>
  )
}

export default Badge
